import React from "react";
import { cn } from "../../lib/utils";
import CommonTooltip from "./CommonTooltip";

// Same bubble sizing the Security screen's Protection History button uses.
const BUTTON_TOOLTIP_CLASS = "w-max max-w-[min(12rem,calc(100vw-1.5rem))] [text-wrap:balance]";

const CommonButton = ({
  children,
  onClick,
  disabled = false,
  className = "",
  wrapperClassName = "",
  variant = "primary",
  colorIntent,
  type,
  theme,
  tooltipText,
  tooltipPosition = "top",
  // Extra classes for the tooltip bubble (e.g. a narrower max-width); merged after the defaults.
  tooltipClassName = "",
  size: _size,
  ...rest
}) => {
  const buttonStyles = {
    primary:
      "bg-[#236DB4] text-white hover:bg-[#1d5a96] focus:ring-blue-300 dark:bg-[#236DB4] dark:hover:bg-[#1d5a96] dark:focus:ring-blue-800",
    success:
      "bg-[#45B369] text-white hover:bg-[#3aa05b] focus:ring-green-300 dark:bg-[#45B369] dark:hover:bg-[#3aa05b] dark:focus:ring-green-700",
    danger:
      "bg-[#FF0000] text-white hover:bg-[#e60000] focus:ring-red-300 dark:bg-[#FF0000] dark:hover:bg-[#e60000] dark:focus:ring-red-700",
    warning:
      "bg-[#FF9F29] text-white hover:bg-[#f28d12] focus:ring-amber-400 dark:bg-[#FF9F29] dark:hover:bg-[#f28d12]",
    download:
      "bg-[#236DB4] text-white focus:ring-blue-300 dark:bg-[#236DB4] dark:focus:ring-blue-800",
    gray: "bg-gray-500 text-white focus:ring-gray-300",
    blue: "bg-[#236DB4] text-white focus:ring-blue-400 dark:bg-[#236DB4]",
    red: "bg-[#FF0000] text-white focus:ring-red-300 dark:bg-[#FF0000] dark:focus:ring-red-800",
    outline:
      "bg-white text-gray-700 border border-gray-300 shadow-sm hover:bg-gray-50 focus:ring-gray-300 dark:bg-white dark:text-gray-800",
  };

  const themeStyles =
    theme === "dark"
      ? "dark-mode"
      : theme === "high-contrast"
        ? " border bg-black text-white"
        : "";

  const buttonClassName = cn(
    "inline-flex items-center justify-center gap-2 text-sm font-medium rounded-lg focus:ring-0 focus:outline-none transition-all box-border min-h-[36px] h-9 px-3.5 py-0",
    buttonStyles[variant] ?? buttonStyles.primary,
    themeStyles,
    disabled && "opacity-60 cursor-not-allowed",
    className
  );

  const button = (
    <button
      type={type}
      disabled={disabled}
      className={buttonClassName}
      onClick={onClick}
      {...rest}
      data-button-intent={colorIntent}
    >
      {children}
    </button>
  );

  // The tooltip is the shared blue CommonTooltip, so every button's hint looks the same
  // as everywhere else in the app. Layout classes go on its trigger wrapper.
  if (!tooltipText) {
    return <div className={cn("relative inline-block", wrapperClassName)}>{button}</div>;
  }

  return (
    <CommonTooltip
      text={tooltipText}
      position={tooltipPosition}
      disableDefaultDescription
      className={cn("relative inline-block", wrapperClassName)}
      tooltipClassName={cn(BUTTON_TOOLTIP_CLASS, tooltipClassName)}
      fitContent
      clampToViewport
      hoverOnly
    >
      {button}
    </CommonTooltip>
  );
};

export default CommonButton;
